import * as React from "react";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeletionModal from "../components/modals/DeletionModal";
import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import DetailedDataModal from "../components/modals/DetailedDataModal";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  deletePublicAssessmentSubmission,
  fetchAllPublicAssessmentData,
} from "../helpers/apiRequests";
import { CGI_BLACK, CGI_RED } from "../helpers/enums";
import { Box, Container, Divider, IconButton, Typography } from "@mui/material";

export default function ViewPublicParticipantData({ language }) {
  const { t } = useTranslation();

  const { selectedAssessmentId } = useParams();

  const [publicAssessmentData, setPublicAssessmentData] = React.useState([]);

  async function fetchData() {
    setOpenLoading(true);
    try {
      const fetchedData = await fetchAllPublicAssessmentData(
        selectedAssessmentId
      );
      setPublicAssessmentData(fetchedData);
    } catch {
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  async function deletePublicCustomer(toDeleteId) {
    try {
      await deletePublicAssessmentSubmission(toDeleteId);
      setPublicAssessmentData((prev) =>
        prev.filter((customer) => customer.id !== toDeleteId)
      );
      setNotificationMessage(t("itemDeleted!"));
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  React.useEffect(() => {
    fetchData();
  }, []);

  const [openNotification, setOpenNotification] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState("");

  const [openLoading, setOpenLoading] = React.useState(false);

  const rowWidth = 1350;

  const cellWidths = {
    mail: 160 / rowWidth,
    name: 160 / rowWidth,
    organization: 160 / rowWidth,
    phone: 160 / rowWidth,
    interestedIn: 160 / rowWidth,
    consentMails: 160 / rowWidth,
    createdAt: 160 / rowWidth,
  };

  const tableHeadingSx = { fontWeight: 500 };

  const [openDetails, setOpenDetails] = React.useState(false);
  const [detailledData, setDetailledData] = React.useState(false);

  const [openDeletePublicCustomer, setOpenDeletePublicCustomer] =
    React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState("");

  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <Box
        sx={{
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            minWidth: 1450,
            maxWidth: 1450,
            maxHeight: "80vh",
          }}
        >
          <Typography sx={{ fontSize: 20 }}>
            {t("dataOfAllParticipants")}
          </Typography>
          <Divider sx={{ my: 2, color: CGI_RED, borderBottom: 2 }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              ml: 2,
              height: 40,
            }}
          >
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.name,
                maxWidth: cellWidths.name,
              }}
              noWrap={true}
            >
              Name
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.mail,
                maxWidth: cellWidths.mail,
              }}
              noWrap={true}
            >
              Mail
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.organization,
                maxWidth: cellWidths.organization,
              }}
              noWrap={true}
            >
              {t("organisationCompany")}
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.phone,
                maxWidth: cellWidths.phone,
              }}
              noWrap={true}
            >
              {t("phone")}
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.interestedIn,
                maxWidth: cellWidths.interestedIn,
              }}
              noWrap={true}
            >
              {t("interestedIn")}
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.consentMails,
                maxWidth: cellWidths.consentMails,
              }}
              noWrap={true}
            >
              {t("consentMailing")}
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
            <Typography
              sx={{
                ...tableHeadingSx,
                minWidth: cellWidths.createdAt,
                maxWidth: cellWidths.createdAt,
              }}
              noWrap={true}
            >
              {t("createdAt")}
            </Typography>
          </Box>
          <Divider sx={{ borderBottom: 2, borderColor: CGI_BLACK }} flexItem />
          {publicAssessmentData.map((customer, index) => (
            <Box key={index}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  pl: 2,
                  "&:hover": {
                    cursor: "pointer",
                    bgcolor: "#22222211",
                  },
                }}
                onClick={() => {
                  setDetailledData(customer);
                  setOpenDetails(true);
                }}
              >
                <Typography
                  sx={{
                    minWidth: cellWidths.name,
                    maxWidth: cellWidths.name,
                  }}
                >
                  {customer.customerName}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.mail,
                    maxWidth: cellWidths.mail,
                  }}
                >
                  {customer.customerMail}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.organization,
                    maxWidth: cellWidths.organization,
                  }}
                >
                  {customer.customerOrganization}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.phone,
                    maxWidth: cellWidths.phone,
                  }}
                >
                  {customer.customerPhone}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.interestedIn,
                    maxWidth: cellWidths.interestedIn,
                  }}
                >
                  {customer?.evaluationData?.interestedIn?.join(", ") || "-"}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.interestedIn,
                    maxWidth: cellWidths.interestedIn,
                  }}
                >
                  {customer?.consentMarketingMails ? t("yes") : t("no")}
                </Typography>
                <Divider flexItem orientation="vertical" sx={{ mx: 2 }} />
                <Typography
                  sx={{
                    minWidth: cellWidths.createdAt,
                    maxWidth: cellWidths.createdAt,
                  }}
                >
                  {new Date(customer.createdAt).toLocaleString(
                    t("timeSettings"),
                    {
                      timeZone: "Europe/Berlin",
                    }
                  )}
                </Typography>{" "}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDeleteId(customer.id);
                    setOpenDeletePublicCustomer(true);
                  }}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Box>
              <Divider flexItem />
            </Box>
          ))}
          <DeletionModal
            open={openDeletePublicCustomer}
            setOpen={setOpenDeletePublicCustomer}
            deleteFunction={deletePublicCustomer}
            safeDelete={true}
            itemClass={"Public Customer"}
            id={toDeleteId}
          />
          <LoadingModal open={openLoading} />
          <NotificationSnackbar
            message={notificationMessage}
            open={openNotification}
            setOpen={setOpenNotification}
          />
          <DetailedDataModal
            open={openDetails}
            setOpen={setOpenDetails}
            language={language}
            data={detailledData}
          />
        </Box>
      </Box>
    </Container>
  );
}
